import { BaseLandlordSurvey } from "api/landlord-surveys/types/types";
import { landlordSurveyApi } from "api/landlord-surveys/landlord-surveys";
import { Card } from "shared/components/Card";
import { DateTime } from "luxon";
import { Badge, BadgeSize, BadgeVariant, Show } from "shared/components";
import {
  ContextMenuPopover,
  type Action
} from "shared/components/ContextMenuPopover";
import { ConfirmDialog } from "shared/components/modals/ConfirmDialog";
import { useMemo, useState, FC } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Delete01Icon } from "@hugeicons-pro/core-stroke-rounded";

type SurveyCardProps = BaseLandlordSurvey & { surveysCount: number };

export const SurveyCard: FC<SurveyCardProps> = (props) => {
  const { surveysCount, ...survey } = props;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["LANDLORD_SURVEYS"] });

  const { mutate: makeDefault } = useMutation({
    mutationFn: () => landlordSurveyApi.makeDefault(survey.identifier),
    onSuccess: () => {
      invalidate();
      toast.success("Survey set as default");
    },
    onError: () => toast.error("Failed to set default survey")
  });

  const { mutate: duplicate } = useMutation({
    mutationFn: () => landlordSurveyApi.duplicateSurvey(survey.identifier),
    onSuccess: () => {
      invalidate();
      toast.success("Survey duplicated");
    },
    onError: () => toast.error("Failed to duplicate survey")
  });

  const { mutate: deleteSurvey, isPending: isDeleting } = useMutation({
    mutationFn: () => landlordSurveyApi.deleteSurvey(survey.identifier),
    onSuccess: () => {
      invalidate();
      setDeleteOpen(false);
      toast.success("Survey deleted");
    },
    onError: () => toast.error("Failed to delete survey")
  });

  const actions = useMemo((): Action[] => {
    const list: Action[] = [];
    if (!survey.is_default)
      list.push({
        title: "Make default",
        onClick: () => makeDefault()
      });
    list.push(
      {
        title: "Edit",
        onClick: () => {
          navigate(`/landlord-surveys/${survey.identifier}`);
        }
      },
      {
        title: "Duplicate",
        onClick: () => duplicate()
      }
    );
    if (surveysCount > 1)
      list.push({
        title: "Delete",
        onClick: () => setDeleteOpen(true),
        className: "text-danger-500"
      });
    return list;
  }, [
    navigate,
    survey.identifier,
    survey.is_default,
    surveysCount,
    makeDefault,
    duplicate
  ]);

  const updatedAtText = DateTime.fromISO(survey.updated_at).toFormat(
    "MMM d, yyyy"
  );

  return (
    <>
      <Card
        className="flex h-full min-h-0 w-full min-w-0 flex-col"
        linkTo={`/landlord-surveys/${survey.identifier}`}
      >
        <header className="flex w-full shrink-0 items-center justify-between gap-2 border-b border-alpha-40 px-6 py-3">
          <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
            <h2 className="truncate text-base font-semibold text-content-1000">
              {survey.name}
            </h2>
            <Show if={survey.is_default}>
              <Badge
                size={BadgeSize.SM}
                className="shrink-0 !rounded-md"
                variant={BadgeVariant.OUTLINE}
              >
                Default
              </Badge>
            </Show>
          </div>
          <div className="shrink-0 -mr-3">
            <ContextMenuPopover borderLess actions={actions} />
          </div>
        </header>
        <footer className="flex shrink-0 items-center justify-between gap-2 px-6 py-3">
          <Badge
            className="shrink-0"
            size={BadgeSize.BASE}
            variant={BadgeVariant.PRIMARY}
          >
            {survey.questions_count}{" "}
            {survey.questions_count === 1 ? "question" : "questions"}
          </Badge>
          <span className="text-xs font-medium text-content-300">
            {updatedAtText}
          </span>
        </footer>
      </Card>

      <ConfirmDialog
        opened={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => deleteSurvey()}
        variant="danger"
        icon={{ icon: Delete01Icon }}
        title="Delete survey template"
        description={`Are you sure you want to delete "${survey.name}"? This action cannot be undone.`}
        cancelButton={{ label: "Cancel" }}
        confirmButton={{ label: "Delete", loading: isDeleting }}
      />
    </>
  );
};
